// Word Bomb Party Server Engine
export class WordBombEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.prompts = [
      'ST', 'IN', 'RE', 'PL', 'CH', 'BL', 'TR', 'GR', 'CL', 'TH',
      'EA', 'OU', 'AN', 'EN', 'AR', 'OR', 'ER', 'SH', 'PR', 'SP'
    ];
    this.botDictionary = {
      'ST': ['STAR', 'STOP', 'START', 'STAY', 'FAST', 'POST', 'PAST', 'LAST', 'BEST', 'RUST', 'STONE', 'STREET'],
      'IN': ['WIND', 'SPIN', 'KING', 'RING', 'LINE', 'MINE', 'PINE', 'RAIN', 'TRAIN', 'COIN', 'JOIN', 'FIND'],
      'RE': ['REST', 'READ', 'FIRE', 'WIRE', 'TIRE', 'CARE', 'BARE', 'DREAM', 'GREEN', 'FRESH', 'REAL', 'FREE'],
      'PL': ['PLAY', 'PLAN', 'PLUG', 'PLOT', 'PLUS', 'APPLE', 'APPLY', 'PLACE', 'PLANT', 'PLATE', 'PLANE', 'REPLY'],
      'CH': ['CHAT', 'CHEF', 'CHOP', 'CHIN', 'RICH', 'EACH', 'MUCH', 'SUCH', 'CHAIN', 'CHESS', 'CHAIR', 'BEACH'],
      'BL': ['BLUE', 'BLOW', 'BLAST', 'BLADE', 'ABLE', 'TABLE', 'CABLE', 'BLACK', 'BLOCK', 'BLANK', 'BLEND', 'BLOOM'],
      'TR': ['TREE', 'TRIP', 'TRAIN', 'TRUE', 'STAR', 'TRUCK', 'TRACK', 'TRACE', 'TRUST', 'TREAT', 'EXTRA', 'INTRO'],
      'GR': ['GROW', 'GREAT', 'GREEN', 'GRAB', 'GRASS', 'GROUP', 'GRADE', 'GRAIN', 'GRAND', 'GRAPE', 'AGREE', 'ANGRY'],
      'CL': ['CLAP', 'CLOUD', 'CLEAN', 'CLUB', 'CLEAR', 'CLOCK', 'CLOSE', 'CLASS', 'CLIFF', 'CLIMB', 'CYCLE', 'UNCLE'],
      'TH': ['THAT', 'THIS', 'THINK', 'WITH', 'BOTH', 'MATH', 'PATH', 'NORTH', 'SOUTH', 'TEETH', 'TRUTH', 'EARTH'],
      'EA': ['TEAM', 'BEAR', 'MEAT', 'EASY', 'DEAL', 'BEAT', 'HEAT', 'LEAF', 'PEAK', 'SEAT', 'CLEAR', 'BREAD'],
      'OU': ['LOUD', 'SOUP', 'POUR', 'FOUR', 'ABOUT', 'ROUND', 'SOUND', 'MOUSE', 'HOUSE', 'CLOUD', 'YOUTH', 'TOUCH'],
      'AN': ['LAND', 'SAND', 'HAND', 'PANTS', 'PLANT', 'DANCE', 'STAND', 'BLANK', 'GIANT', 'CLEAN', 'PIANO', 'OCEAN'],
      'EN': ['SEND', 'BEND', 'OPEN', 'TENT', 'SEVEN', 'GREEN', 'QUEEN', 'SPEND', 'BLEND', 'FRIEND', 'LISTEN', 'GARDEN'],
      'AR': ['DARK', 'PARK', 'CARD', 'WARM', 'FARM', 'STAR', 'HARD', 'YARD', 'SHARK', 'START', 'SMART', 'PARTY'],
      'OR': ['WORD', 'PORT', 'BORN', 'CORN', 'DOOR', 'FORK', 'HORN', 'LORD', 'NORM', 'STORM', 'SWORD', 'SPORT'],
      'ER': ['WATER', 'TIGER', 'RIVER', 'HERO', 'POWER', 'SUPER', 'PAPER', 'TOWER', 'NEVER', 'AFTER', 'ENTER', 'ORDER'],
      'SH': ['SHIP', 'FISH', 'WASH', 'SHINE', 'SHARP', 'SHARE', 'SHEEP', 'SHOCK', 'SHOOT', 'SHOUT', 'CRASH', 'FLASH'],
      'PR': ['PRAY', 'PRICE', 'PROUD', 'PRIZE', 'PRESS', 'PRIME', 'PRINT', 'PROOF', 'SPARK', 'SPRING', 'SPREAD', 'PROMISE'],
      'SP': ['SPOT', 'SPEED', 'SPACE', 'SPIN', 'SPEAK', 'SPARK', 'SPOON', 'SPORT', 'SPLIT', 'GASP', 'WASP', 'CRISP']
    };
    this.currentPrompt = '';
    this.currentTurnIndex = 0;
    this.eliminatedIds = new Set();
    this.lives = {}; // playerId -> 2
    this.usedWords = new Set();
    this.bombTimer = 7.0;
    this.timerInterval = null;
    this.botTimeout = null;
  }

  start() {
    this.eliminatedIds.clear();
    this.usedWords.clear();

    // Preserve bot flag and auto-fill bots if solo
    this.room.players = this.room.players.map(p => ({
      ...p,
      isBot: !!p.isBot
    }));

    if (this.room.players.length < 2) {
      this.room.players.push(
        { id: 'bot_wb1', name: '🤖 LexiBot', avatar: '🤖', isBot: true },
        { id: 'bot_wb2', name: '🤖 Wordy', avatar: '🦊', isBot: true }
      );
    }

    this.room.players.forEach((p) => { this.lives[p.id] = 2; });
    this.currentTurnIndex = 0;
    this.nextTurn();
  }

  nextTurn() {
    if (this.botTimeout) {
      clearTimeout(this.botTimeout);
      this.botTimeout = null;
    }

    const alivePlayers = this.room.players.filter((p) => !this.eliminatedIds.has(p.id));
    if (alivePlayers.length <= 1) {
      this.io.to(this.room.code).emit('wb_gameOver', {
        winner: alivePlayers[0] || null
      });
      return;
    }

    const currentPlayer = alivePlayers[this.currentTurnIndex % alivePlayers.length];
    this.currentPrompt = this.prompts[Math.floor(Math.random() * this.prompts.length)];
    this.bombTimer = Math.max(3.5, 7.0 - this.usedWords.size * 0.1);

    this.io.to(this.room.code).emit('wb_newTurn', {
      playerId: currentPlayer.id,
      playerName: currentPlayer.name,
      prompt: this.currentPrompt,
      timeLimit: Math.round(this.bombTimer),
      lives: this.lives
    });

    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.bombTimer -= 0.1;
      this.io.to(this.room.code).emit('wb_tick', { timer: Math.max(0, this.bombTimer).toFixed(1) });

      if (this.bombTimer <= 0) {
        this.explodeBomb(currentPlayer);
      }
    }, 100);

    // If current player is AI bot, schedule bot word submission
    if (currentPlayer.isBot) {
      const delay = 1800 + Math.random() * 1400;
      this.botTimeout = setTimeout(() => {
        const words = this.botDictionary[this.currentPrompt] || [];
        const validUnused = words.filter(w => !this.usedWords.has(w));
        // 90% chance to submit a valid word, 10% chance to fail under pressure
        if (validUnused.length > 0 && Math.random() > 0.1) {
          const chosenWord = validUnused[Math.floor(Math.random() * validUnused.length)];
          this.submitWord(currentPlayer.id, chosenWord);
        }
      }, delay);
    }
  }

  submitWord(playerId, word) {
    if (this.botTimeout) {
      clearTimeout(this.botTimeout);
      this.botTimeout = null;
    }

    const alivePlayers = this.room.players.filter((p) => !this.eliminatedIds.has(p.id));
    const currentPlayer = alivePlayers[this.currentTurnIndex % alivePlayers.length];
    if (!currentPlayer || currentPlayer.id !== playerId) return { valid: false };

    const cleanWord = word.trim().toUpperCase();
    if (cleanWord.length < 3) return { valid: false, reason: 'Too short!' };
    if (!cleanWord.includes(this.currentPrompt)) return { valid: false, reason: `Must contain "${this.currentPrompt}"!` };
    if (this.usedWords.has(cleanWord)) return { valid: false, reason: 'Word already used!' };

    this.usedWords.add(cleanWord);
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.io.to(this.room.code).emit('wb_wordSuccess', {
      playerId,
      word: cleanWord,
      prompt: this.currentPrompt
    });

    this.currentTurnIndex++;
    setTimeout(() => this.nextTurn(), 1200);
    return { valid: true };
  }

  explodeBomb(player) {
    if (this.botTimeout) {
      clearTimeout(this.botTimeout);
      this.botTimeout = null;
    }
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.lives[player.id] = (this.lives[player.id] || 2) - 1;

    const isDead = this.lives[player.id] <= 0;
    if (isDead) this.eliminatedIds.add(player.id);

    this.io.to(this.room.code).emit('wb_exploded', {
      playerId: player.id,
      playerName: player.name,
      livesLeft: this.lives[player.id],
      isEliminated: isDead
    });

    this.currentTurnIndex++;
    setTimeout(() => this.nextTurn(), 2500);
  }

  destroy() {
    if (this.botTimeout) {
      clearTimeout(this.botTimeout);
      this.botTimeout = null;
    }
    if (this.timerInterval) clearInterval(this.timerInterval);
  }
}
